import {
  ContentComment,
  Course,
  CourseCertificate,
  FilterCourse,
  Instructor,
  User,
  UserCourse
} from "../../../domain/entities/entities";
import { CourseRepository } from "../repository";
import http from "../http/index";
import { Receipt } from "../../../domain/entities/shop/order/receipt";
import { wrapErrorHandler } from "../utils/wrap_error_handler";
import { ListResponse } from "../../../domain/entities/shop/product/listResponse";
import { Order } from "../../../domain/entities/order";

function encodeFilterToURL(filter: FilterCourse): string {
  /** Concatenates the array of category ids with the string '&' and strips all spaces. */
  const schools = filter.schools
    .map(id => `school=${id}`)
    .join("&")
    .trim();

  /** Concatenates the array of shops ids with the string '&' and strips all spaces. */
  const courseTracks = filter.courseTracks
    .map(id => `courseTrack=${id}`)
    .join("&")
    .trim();

  /** Variable that receives all formatted parameters.
   * Ex.: category=1&category=3&shop=8&interval=(10,20) */
  let params = "";

  /** Checks if there are categories inside the array.
   * If it exists, it is checked if there are already parameters in the URl.
   * If so, the string '&' is added.
   * If it doesn't mean it is the first parameter of the URL then the string '?' is added. */
  if (filter.schools.length) {
    params += params.length ? `&${schools}` : `?${schools}`;
  }

  if (filter.courseTracks.length) {
    params += params.length ? `&${courseTracks}` : `?${courseTracks}`;
  }

  return params;
}

class _CourseRepository implements CourseRepository {
  async get(courseID: number): Promise<Course> {
    return wrapErrorHandler<Course>(async () => {
      const response = await http.get(`/api/decola/course/list/id/${courseID}`);
      return response.data.course;
    });
  }

  async list(
    order: Order,
    filter: FilterCourse
  ): Promise<ListResponse<Course>> {
    return wrapErrorHandler<ListResponse<Course>>(async () => {
      const currentBodyWidth = document.body.offsetWidth;
      const limit = currentBodyWidth >= 1400 ? 25 : 15;

      const response = await http.get(
        `/api/decola/course/list${encodeFilterToURL(filter)}`,
        {
          params: {
            page: order.page,
            limit,
            search: order.search,
          },
        }
      );

      return response.data;
    });
  }

  async listNewCourses(): Promise<Course[]> {
    return wrapErrorHandler<Course[]>(async () => {
      const response = await http.get("/api/course/list/new");
      return response.data.courses;
    });
  }

  async listRecommendedCourses(): Promise<Course[]> {
    return wrapErrorHandler<Course[]>(async () => {
      const response = await http.get(
        "/api/decola/list/courses/recommendation"
      );
      return response.data.courses;
    });
  }

  listByCourseTrack(
    order: Order,
    courseTrackID: number
  ): Promise<ListResponse<Course>> {
    return wrapErrorHandler<ListResponse<Course>>(async () => {
      const response = await http.get(
        `/api/decola/course/courseTrack/${courseTrackID}`,
        {
          params: {
            page: order.page,
            limit: 15
          }
        }
      );

      return response.data;
    });
  }

  enrollInCourse(courseID: number): Promise<unknown> {
    return wrapErrorHandler<unknown>(async () => {
      const response = await http.post(`/api/decola/enroll/${courseID}`);
      return response.data;
    });
  }

  userCourseSituation(courseID: number): Promise<UserCourse> {
    return wrapErrorHandler<UserCourse>(async () => {
      const response = await http.get(`/api/decola/enroll/${courseID}`);
      return response.data;
    });
  }

  finishCourse(courseID: number): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      const response = await http.post(`/api/decola/course/finish/${courseID}`);
      return response.data;
    });
  }

  async createReceipt(orderID: number): Promise<Receipt> {
    return wrapErrorHandler<Receipt>(async () => {
      const response = await http.post(`/api/decola/receipt/${orderID}`);
      return response.data;
    });
  }

  getCertificate = (courseID: number): Promise<CourseCertificate> => {
    return wrapErrorHandler<CourseCertificate>(async () => {
      const response = await http.get(`/api/decola/certificate/${courseID}`);
      return response.data;
    });
  };

  async listBySearch(order: Order): Promise<ListResponse<Course>> {
    return wrapErrorHandler<ListResponse<Course>>(async () => {
      const response = await http.get(`/api/decola/course/globalSearch`, {
        params: {
          page: order.page,
          limit: order.limit,
          search: order.search
        }
      });
      return response.data;
    });
  }

  getComments(
    courseId: number,
    order: Order
  ): Promise<ListResponse<ContentComment>> {
    return wrapErrorHandler<ListResponse<ContentComment>>(async () => {
      const response = await http.get(`/api/decola/rate/${courseId}`, {
        params: {
          page: order.pageDefault,
          limit: 15,
          search: order.search
        }
      });
      return response.data;
    });
  }

  getInstructors(courseID: number): Promise<Instructor[]> {
    return wrapErrorHandler<Instructor[]>(async () => {
      const response = await http.get(
        `/api/decola/courses/${courseID}/instructors`
      );
      return response.data;
    });
  }

  leaveCourse(courseID: number): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      const response = await http.post(
        `/api/decola/remove/progress/course/${courseID}`
      );
      return response.data;
    });
  }

  indicationCourse(courseID: number): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      const response = await http.post(
        `/api/decola/indication/training/${courseID}`
      );
      return response.data;
    });
  }

  listIndicationsCourses(): Promise<ListResponse<Course[]>> {
    return wrapErrorHandler<ListResponse<Course[]>>(async () => {
      const response = await http.get("/api/decola/list/indication");
      return response.data;
    });
  }

  loadIndicationByUsers(courseID: number): Promise<ListResponse<User[]>> {
    return wrapErrorHandler<ListResponse<User[]>>(async () => {
      const response = await http.get(
        `/api/decola/list/user/indication/training/${courseID}`
      );
      return response.data;
    });
  }

  addFavoriteTraining(courseID: number): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      const response = await http.post(
          `api/decola/favorite/training/${courseID}`
      );
      return response.data;
    });
  }

  removeFavoriteTraining(courseID: number): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      const response = await http.delete(
          `api/decola/remove/favorite/training/${courseID}`
      );
      return response.data;
    });
  }

  async listFavoriteTrainings(order: Order): Promise<ListResponse<Course[]>> {
    return wrapErrorHandler<ListResponse<Course[]>>(async () => {
      const response = await http.get(`/api/decola/list/favorite`, {
        params: {
          page: order.page,
          limit: 30,
          search: order.search,
          orderBy: order.orderBy,
          ordinationAsc: order.isAsc,
          type: order.type,
        }
      });
      return response.data;
    });
  }

}

export const interfaceCourseRepository: CourseRepository = new _CourseRepository();
